import { useState, useCallback, useMemo } from 'react';
import type { GraphData, GraphNode } from '@packages/shared';

interface UseSearchOptions {
  enableGrayOut?: boolean;
  connectionDepth?: number;
  onFocusNode?: (nodeId: string) => void;
  onResetGrayOut?: () => void;
}

export function useSearch(
  currentData: GraphData,
  options: UseSearchOptions = {}
) {
  const {
    enableGrayOut = true,
    connectionDepth = 1,
    onFocusNode,
    onResetGrayOut
  } = options;

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<GraphNode[]>([]);
  const [currentHighlightedNode, setCurrentHighlightedNode] = useState<string | null>(null);

  // 搜索处理
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const lowerQuery = query.toLowerCase();
    const results = currentData.nodes.filter(node =>
      node.label.toLowerCase().includes(lowerQuery)
    ).slice(0, 10);

    setSearchResults(results);
  }, [currentData.nodes]);

  // 查找指定阶数的连接节点
  const findConnectedNodes = useCallback((startNodeId: string, depth: number): Set<string> => {
    const visited = new Set<string>([startNodeId]);
    const currentLevel = new Set<string>([startNodeId]);

    for (let level = 0; level < depth; level++) {
      const nextLevel = new Set<string>();

      for (const nodeId of currentLevel) {
        // 查找所有与当前节点相连的边
        currentData.edges.forEach(edge => {
          let connectedNodeId: string | null = null;

          if (edge.from === nodeId && !visited.has(edge.to)) {
            connectedNodeId = edge.to;
          } else if (edge.to === nodeId && !visited.has(edge.from)) {
            connectedNodeId = edge.from;
          }

          if (connectedNodeId) {
            visited.add(connectedNodeId);
            nextLevel.add(connectedNodeId);
          }
        });
      }

      // 更新当前层级为下一层级
      currentLevel.clear();
      nextLevel.forEach(nodeId => currentLevel.add(nodeId));

      if (currentLevel.size === 0) break;
    }

    return visited;
  }, [currentData.edges]);

  // 应用搜索置灰效果
  const applySearchGrayOut = useCallback((targetNodeId: string): GraphData => {
    if (!enableGrayOut) return currentData;

    const connectedNodes = findConnectedNodes(targetNodeId, connectionDepth);

    // 创建更新后的数据
    const updatedNodes = currentData.nodes.map(node => ({
      ...node,
      color: connectedNodes.has(node.id)
        ? node.color  // 保持原颜色
        : '#d1d5db'   // 置灰色
    }));

    const updatedEdges = currentData.edges.map(edge => ({
      ...edge,
      color: (connectedNodes.has(edge.from) && connectedNodes.has(edge.to))
        ? edge.color || '#666666'  // 保持原颜色
        : '#e5e7eb'                // 置灰色
    }));

    setCurrentHighlightedNode(targetNodeId);

    return { nodes: updatedNodes, edges: updatedEdges };
  }, [currentData, enableGrayOut, findConnectedNodes, connectionDepth]);

  // 聚焦到指定节点
  const focusOnNode = useCallback((nodeId: string) => {
    console.log('Focusing on node:', nodeId);
    onFocusNode?.(nodeId);
    // 清理搜索状态，像Vue demo一样
    setSearchQuery('');
    setSearchResults([]);
  }, [onFocusNode]);

  // 重置搜索置灰效果
  const resetSearchGrayOut = useCallback(() => {
    console.log('Resetting search gray out effect');
    setCurrentHighlightedNode(null);
    setSearchQuery('');
    setSearchResults([]);
    onResetGrayOut?.();
  }, [onResetGrayOut]);

  // 计算统计信息
  const statistics = useMemo(() => {
    // 节点类型分布
    const nodeTypeStats: Record<string, number> = {};
    currentData.nodes.forEach(node => {
      nodeTypeStats[node.type] = (nodeTypeStats[node.type] || 0) + 1;
    });

    // 连接度统计
    const degrees: Record<string, number> = {};
    currentData.nodes.forEach(node => {
      degrees[node.id] = 0;
    });

    currentData.edges.forEach(edge => {
      degrees[edge.from] = (degrees[edge.from] || 0) + 1;
      degrees[edge.to] = (degrees[edge.to] || 0) + 1;
    });

    const degreeValues = Object.values(degrees);
    const averageDegree = degreeValues.length > 0 
      ? (degreeValues.reduce((sum, degree) => sum + degree, 0) / degreeValues.length).toFixed(2)
      : '0';
    const maxDegree = degreeValues.length > 0 ? Math.max(...degreeValues) : 0;
    const minDegree = degreeValues.length > 0 ? Math.min(...degreeValues) : 0;

    return {
      nodeTypeStats,
      degrees,
      averageDegree,
      maxDegree,
      minDegree
    };
  }, [currentData]);

  return {
    searchQuery,
    searchResults,
    currentHighlightedNode,
    statistics,
    handleSearch,
    focusOnNode,
    applySearchGrayOut,
    resetSearchGrayOut,
    setSearchQuery,
    setSearchResults
  };
}